// 戦闘の進行（端末側）。core の battle API（createBattle / advance / apply / autoCommand / isOver / result）で進め、
// 成功したコマンドを記録する。決着したら commands をサーバーの /api/battle/finish に送って検証してもらう。
// 中断：コマンド列を localStorage に保存し、同じ setup から再生して復元する（決定論なので同じ盤面に戻る）。
import { BATTLE_CONFIG, ENEMIES, itemInfo, turnLimitInfo, shikiFormName, statusInfo, surgeLabel, surgeStageName, type BattleResult, type BattleSetup, type BuildingType, type CharId, type Command, type ItemId, type Side, type TargetRef } from '@hyakki/core';
import { sfx, vary, type SfxKey } from '../audio';
import type { FxKind } from './fx';
import type { Battle, BattleApi, BattleEvent } from '../core-bridge';
import { BUILDING_INFO } from '../assets';
import { companionEventKey, companionEventText, companionHas } from '../ui/companion';
import { legalOf, refCell, sameRef, snapshot, surgeView, type FxLook, type Legal, type VBoard, type VUnit } from './adapter';
import { adjacent, dist } from './hex';
import type { InputHandlers } from './input';
import { emptyHighlights, type BoardView } from './render';

/** 攻撃の見た目（battle/fx.ts の attackRecipe）→ [通常攻撃の音, 術の音]。敵の爪・妖気の弾の術は共通の術の音 */
const ATTACK_SE: Partial<Record<FxKind | 'none', [SfxKey, SfxKey]>> = {
  akari: ['atkAkari', 'skillAkari'],
  kazamaru: ['atkKazamaru', 'skillKazamaru'],
  suzune: ['atkSuzune', 'skillSuzune'],
  yuzuha: ['atkYuzuha', 'skillYuzuha'],
  claw: ['atkClaw', 'skill'],
  orb: ['atkOrb', 'skill'],
};

export type Phase = 'start' | 'rolled' | 'moved';

export interface TurnUI {
  phase: Phase;
  legal: Legal;
  sel: number | null;
  route: number[] | null;
  target: TargetRef | null;
  mode: 'attack' | 'skill' | 'ougi' | 'cast' | null;
  build: BuildingType | null;
  /** 術（自分を中心に放つもの）を選んでいる：狙うマス（縮地の着地点・送り火のマス） */
  castCell?: number | null;
  forecast: import('@hyakki/core').Forecast | null;
}

export interface Hooks {
  changed(): void; // HUD を描き直す
  /** 賽。dice があれば賽3つ（rules 2）：3つの目・役・効果 */
  dice(value: number, quick: boolean, three?: { dice: number[]; role?: string; perks?: string[]; enemy?: boolean }): Promise<void>;
  /** 術の賽（rules 2）：術・奥義を放つときの3つの目と倍率 */
  skillDice?(dice: number[], mult: number, zorome: boolean, quick: boolean): Promise<void>;
  /** ougi＝妖力の奥義（大きなカットイン） */
  cutIn(name: string, skill: string, side: Side | 'neutral', charId?: FxLook, ougi?: boolean, enemyId?: string): Promise<void>;
  banner(text: string, sub?: string): Promise<void>;
  /** 敵の魂が撃破した所から妖力のゲージへ吸い込まれる（待たない） */
  soul(cell: number): void;
  log(text: string): void;
  info(cell: number | null): void; // 情報カード
  error(text: string): void;
  over(result: BattleResult, commands: Command[]): void;
  saved(): void;
  /** 妖力の段階上昇（自分はカットイン、敵は小さな告知）。heal は続く回復の量（無ければ 0） */
  surge?(e: SurgeEvent, heal: number, unit: VUnit | undefined): Promise<void>;
  /** 一撃離脱の移動が始まった */
  dash?(e: Extract<BattleEvent, { t: 'dash' }>, unit: VUnit | undefined): Promise<void>;
  /** 式神の奥義（★5。お供の companion イベント ability: 'ougi'）：専用のカットイン */
  ougi?(enemyId: string, level: number, unit: VUnit | undefined): Promise<void>;
}
export type SurgeEvent = Extract<BattleEvent, { t: 'surge' }>;
/** 宝箱の中身の表示名 */
export const CHEST_LABEL: Record<string, string> = { zeni: '銭', reidama: '霊玉', equip: '装備', omamori: 'お守り' };

export interface SavedBattle {
  v: 1;
  sessionId: string | null;
  setup: BattleSetup;
  commands: Command[];
  stageId: number;
  charId: CharId;
  savedAt: number;
  local?: boolean; // サーバーを通さない試合（開発用）
}
const SAVE_KEY = 'hk.battle';
export const loadSaved = (): SavedBattle | null => {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    const s = raw ? (JSON.parse(raw) as SavedBattle) : null;
    return s && s.v === 1 ? s : null;
  } catch {
    return null;
  }
};
export const clearSaved = () => {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* 無視 */
  }
};

const WHY: Record<string, string> = {
  territory: '陣地',
  home: '本陣',
  torii: '鳥居',
  shrine: '祠',
  potion: '薬',
  regen: '再生',
  sanctuary: '聖域',
  skill: '術',
  leech: '吸精',
  surge: '妖力',
  companion: '式神',
  devour: '喰らい',
};

export class BattleController {
  b: Battle;
  board: VBoard;
  commands: Command[] = [];
  ui: TurnUI | null = null;
  auto = false;
  /** 演出省略の高速モード（オート周回）：イベントの演出を再生せず、盤面の同期も間引く */
  turbo = false;
  /** 節電モード：裏で戦闘を進めるが、自分の手番 k の始まりを start + (k-1)×msPerTurn まで待つ（1戦の時間＝手番数×秒÷倍速に合わせる） */
  pace: { start: number; msPerTurn: number } | null = null;
  /** オートの目標（式神候補を必ず倒す・龍脈をすべて取る）。端末のAIの選択だけで、サーバーの検証には影響しない */
  aiOpts: import('@hyakki/core').AiOptions = {};
  paused = false;
  busy = false;
  over: BattleResult | null = null;
  /** 正本はイベント列の終点まで進むため、観戦の案内は再生中のイベントから作る。 */
  activity: { text: string; side: Side | 'neutral' } = { text: '出陣の支度中', side: 'blue' };
  moment = '';
  /**
   * チュートリアルの一本道：移動できるマス（cells）と狙える相手（target）を絞る。null なら自由。
   * 盤のタップ・なぞり・行動ボタンの対象選びがこれに従う
   */
  lock: { cells?: number[]; target?: TargetRef; mode?: 'attack' | 'skill' | 'ougi' } | null = null;
  /** 妖力の奥義を構えている（メダリオンの長押し。術ボタンが奥義ボタンになる）。放つか、放てなくなれば解ける */
  armed = false;
  /** 直前の攻撃が術か（当たったときの数字・溜めを強める） */
  private lastSkill = false;
  /** 砦：残りの手番の知らせを出した */
  private turnsWarned = false;
  /** 直前に攻撃した側（倒したのが味方なら、敵の魂が妖力のゲージへ吸い込まれる） */
  private lastAttackSide: Side | 'neutral' | null = null;
  private token = 0;
  private resolveInput: (() => void) | null = null;
  private resumeWait: (() => void) | null = null;
  private lastSync = 0;
  private slice = 0;

  constructor(
    readonly api: BattleApi,
    readonly view: BoardView,
    readonly save: Omit<SavedBattle, 'commands' | 'savedAt' | 'v'>,
    readonly terrain: string,
    readonly hooks: Hooks,
    resume: Command[] = [],
  ) {
    this.b = api.createBattle(save.setup);
    // 中断からの復元：同じ順で advance → apply を再生する（apply は必要なら自分で advance する）
    for (const cmd of resume) {
      const r = api.apply(this.b, cmd);
      if (!r.ok) break;
      this.commands.push(cmd);
    }
    this.board = this.snap();
    view.sync(this.board);
  }

  private snap() {
    return snapshot(this.api, this.b, this.terrain);
  }
  get hero() {
    return this.board.units.find((u) => u.kind === 'hero') ?? null;
  }

  start() {
    void this.loop();
  }
  stop() {
    this.token++;
    this.resolveInput?.();
    this.resumeWait?.();
    this.view.flushAnims();
  }

  setPaused(p: boolean) {
    this.paused = p;
    if (p) this.persist();
    else {
      this.resumeWait?.();
      this.resumeWait = null;
    }
    this.hooks.changed();
  }
  setTurbo(on: boolean) {
    this.turbo = on;
    if (!on) {
      // 通常の再生へ戻す：盤面を正本に合わせ直す
      this.board = this.snap();
      this.view.sync(this.board);
      this.hooks.changed();
    }
  }
  setAuto(on: boolean) {
    this.auto = on;
    if (on) {
      this.clearSelection();
      this.ui = null;
      this.view.hl = emptyHighlights();
      this.hooks.info(null);
    }
    if (on && this.resolveInput && !this.busy) {
      const r = this.resolveInput;
      this.resolveInput = null;
      r();
    }
    this.hooks.changed();
  }

  persist() {
    if (this.over) return;
    const s: SavedBattle = { v: 1, ...this.save, commands: this.commands, savedAt: Date.now() };
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(s));
      this.hooks.saved();
    } catch {
      /* 保存できなくても続行 */
    }
  }

  private gate() {
    if (!this.paused) return Promise.resolve();
    return new Promise<void>((r) => (this.resumeWait = r));
  }

  private async loop() {
    const token = ++this.token;
    const alive = () => token === this.token;
    while (alive()) {
      await this.gate();
      if (!alive()) return;
      if (this.api.isOver(this.b)) return this.finish();
      const ev = this.api.advance(this.b);
      await this.play(ev, alive);
      if (!alive()) return;
      if (this.api.isOver(this.b)) continue;
      await this.gate();
      if (!alive()) return;
      if (this.auto) {
        if (this.pace) {
          const due = this.pace.start + Math.max(0, this.b.stats.playerTurns - 1) * this.pace.msPerTurn;
          while (alive() && performance.now() < due) await new Promise<void>((r) => setTimeout(r, Math.min(500, due - performance.now())));
          if (!alive()) return;
          await this.gate();
          if (!alive()) return;
        }
        let cmd = this.api.autoCommand(this.b, this.aiOpts);
        let r = this.api.apply(this.b, cmd);
        if (!r.ok) {
          cmd = { t: 'wait' };
          r = this.api.apply(this.b, cmd);
        }
        if (!r.ok) {
          this.auto = false;
          this.hooks.error(`オートを止めました（${r.error}）`);
          continue;
        }
        this.commands.push(cmd);
        await this.play(r.events, alive);
        // 端末だけの試合（プロローグ）は1手ごとに退避（3手番の短い戦いなので、閉じても手番を失わない）
        if (this.save.local || this.commands.length % (this.turbo ? 40 : 4) === 0) this.persist();
        continue;
      }
      // 手動：1つのコマンドが通るまで待つ（防衛の「次の波へ」の間は、静かなうちは手番終了を続ける）
      this.beginTurn();
      if (this.skipWave && !this.canSkipWave()) {
        this.skipWave = false;
        this.hooks.changed();
      } else if (this.skipWave) this.skipHp = this.hero?.hp ?? 0;
      await new Promise<void>((r) => {
        this.resolveInput = r;
        if (this.skipWave) setTimeout(() => this.skipWave && void this.exec({ t: 'wait' }), 120);
      });
      this.resolveInput = null;
    }
  }

  private finish() {
    if (this.over) return;
    if (this.turbo) {
      this.board = this.snap();
      this.view.sync(this.board);
    }
    this.over = this.api.result(this.b);
    this.ui = null;
    this.view.hl = emptyHighlights();
    clearSaved();
    this.hooks.changed();
    this.hooks.over(this.over, this.commands);
  }

  // ---------- イベント再生 ----------
  private narrate(text: string, side: Side | 'neutral' = 'blue', moment = false) {
    this.activity = { text, side };
    if (moment) this.moment = text;
    this.hooks.changed();
  }

  private async play(events: BattleEvent[], alive: () => boolean) {
    if (this.turbo && this.auto) return this.turboStep();
    for (let i = 0; i < events.length; i++) {
      const e = events[i];
      if (!alive()) return;
      await this.gate();
      try {
        if (e.t === 'surge') {
          // 段階上昇の直後の回復（why: surge）の量をカットインに出す
          const h = events.slice(i + 1, i + 4).find((x) => x.t === 'heal' && x.why === 'surge' && x.id === e.id);
          await this.playSurge(e, h && h.t === 'heal' ? h.amount : 0);
          continue;
        }
        await this.playEvent(e);
      } catch {
        /* 演出の失敗で進行を止めない */
      }
    }
    this.board = this.snap();
    this.view.sync(this.board);
    this.hooks.changed();
  }

  /** 高速モード：演出なし。盤面の同期は約0.15秒ごと、約12msごとに処理を譲って画面を固めない */
  private async turboStep() {
    const now = performance.now();
    if (now - this.lastSync > 150) {
      this.lastSync = now;
      this.board = this.snap();
      this.view.sync(this.board);
      this.hooks.changed();
    }
    if (!this.slice) this.slice = now;
    if (now - this.slice > 12) {
      this.slice = 0;
      await new Promise<void>((r) => setTimeout(r, 0));
    }
  }

  private async playSurge(e: SurgeEvent, heal: number) {
    const v = this.view;
    const u = v.unit(e.id);
    if (e.drop) {
      // 奥義の後：妖力が壱に戻る（演出は無し、表示だけ戻す）
      const fresh = { ...surgeView(this.b, e.side), stage: e.stage, stageName: surgeStageName(e.stage), points: e.points };
      for (const bd of new Set([this.board, v.vm?.board])) if (bd?.surge?.[e.side]) Object.assign(bd.surge[e.side], fresh);
      if (u) u.surge = e.stage;
      this.hooks.log(`${u?.name ?? ''} ${surgeLabel()} ${surgeStageName(e.stage)}に戻った`);
      this.hooks.changed();
      return;
    }
    // 盤面の表示用の妖力（ゲージ・HUD のチップ・札）も先に進める。次の段階までの残りなどは正本から読み直す
    const name = surgeStageName(e.stage);
    const fresh = { ...surgeView(this.b, e.side), stage: e.stage, stageName: name, points: e.points };
    for (const bd of new Set([this.board, v.vm?.board])) if (bd?.surge?.[e.side]) Object.assign(bd.surge[e.side], fresh);
    v.surgeUp(e.id, e.stage);
    sfx('surge', e.side === 'blue' ? 1 : 0.6);
    this.hooks.log(`${u?.name ?? ''} ${surgeLabel()} ${name}（${e.traits.map((t) => t.name).join('・') || '能力上昇'}）`);
    this.narrate(`${u?.name ?? ''}：妖力 ${name}！ ${e.traits.map(t => t.name).join('・') || '能力上昇'}`, e.side, true);
    this.hooks.changed();
    if (this.hooks.surge) await this.hooks.surge(e, heal, u);
    else await v.wait(300);
  }

  private async playEvent(e: BattleEvent) {
    const v = this.view;
    const unit = (id: number) => v.unit(id);
    const targetCell = (t: TargetRef) => (t.kind === 'core' ? this.board.cores[t.side].cell : (unit(t.id)?.cell ?? null));
    switch (e.t) {
      case 'turn': {
        const u = unit(e.id);
        // advance は複数の敵の行動をまとめて返す。リングも再生中の主役に合わせる。
        this.board.current = e.id;
        if (v.vm) v.vm.board.current = e.id;
        this.lastSkill = false;
        this.narrate(`${u?.name ?? '妖'}の手番`, u?.side);
        // 敵将の手番だけ太鼓で合図（自分の手番の合図は「しゅ」と擦れて聞こえ不要とのことで鳴らさない）
        if (u?.kind === 'rival') sfx('turnEnemy', 0.6);
        // 砦（短期決戦）：残り3手番になったら1度だけ知らせる
        if (u?.side === 'blue' && u.kind === 'hero' && !this.turnsWarned && (turnLimitInfo(this.b)?.left ?? Infinity) <= 3) {
          this.turnsWarned = true;
          await this.hooks.banner(`残り${turnLimitInfo(this.b)!.left}手番`, '手番が尽きると負け');
        }
        if (u && u.side !== 'blue') await v.wait(u.kind === 'rival' ? 140 : 50);
        break;
      }
      case 'roll': {
        const u = unit(e.id);
        const role = e.role ? BATTLE_CONFIG.dice3.roles[e.role]?.name : null;
        this.narrate(
          e.dice ? `${u?.name ?? '妖'}：賽 ${e.dice.join('・')}（合計${e.dice.reduce((x, y) => x + y, 0)}）→ 移動${e.value}${role ? `｜${role}！` : ''}` : `${u?.name ?? '妖'}：賽 ${e.value}`,
          u?.side,
          !!role,
        );
        if (e.perks?.length) this.hooks.log(`${u?.name ?? '妖'}：${e.perks.join('・')}`);
        sfx('roll', unit(e.id)?.side === 'blue' ? 1 : 0.6);
        await this.hooks.dice(e.value, unit(e.id)?.side !== 'blue', e.dice ? { dice: e.dice, role: e.role, perks: e.perks, enemy: unit(e.id)?.side !== 'blue' } : undefined);
        sfx('diceResult', unit(e.id)?.side === 'blue' ? 0.9 : 0.5);
        break;
      }
      case 'move': {
        const u = unit(e.id);
        this.narrate(`${u?.name ?? '妖'}：${Math.max(0, e.path.length - 1)}マス移動`, u?.side);
        sfx('move', 0.5, vary());
        await v.moveUnit(e.id, e.path, e.painted, u && u.side !== 'neutral' ? u.side : null);
        break;
      }
      case 'attack': {
        const u = unit(e.id);
        const cell = targetCell(e.target);
        const target = e.target.kind === 'core' ? (e.target.side === 'red' ? '敵本陣' : '自本陣') : unit(e.target.id)?.name ?? '対象';
        this.narrate(`${u?.name ?? '妖'} → ${target}｜${e.skill ?? '攻撃'}`, u?.side);
        if (cell != null) {
          v.focusAttack(e.id, cell);
          await v.wait(150);
        }
        if (e.skill && u) {
          v.popupUnit(e.id, e.skill, e.ougi ? 'skill-name ougi' : 'skill-name');
          if (e.ougi) v.shake(10);
          if (u.kind === 'hero' || u.kind === 'rival') {
            sfx(e.ougi ? 'ougiCharge' : 'cutin');
            await this.hooks.cutIn(u.name, e.skill, u.side, u.charId ?? u.fx, !!e.ougi, u.enemyId);
          }
        }
        this.lastSkill = !!e.skill;
        this.lastAttackSide = u?.side ?? null;
        // 当たる瞬間：奥義＝大爆発、術＝術の炸裂、通常＝斬撃（音程を少し揺らす）
        const hitSound = () => {
          if (e.ougi) return sfx('ougi');
          // 見た目の種類（狐火・風の刃・大槌・刀・爪・妖気の弾）に合った音。無ければ共通の斬撃・術
          const pair = ATTACK_SE[v.attackKind?.(e.id) ?? 'none'];
          if (e.skill) sfx(pair?.[1] ?? 'skill');
          else sfx(pair?.[0] ?? 'hit', 0.9, vary());
        };
        if (cell != null) await v.attackFx(e.id, cell, !!e.skill, !!e.ougi, hitSound);
        else hitSound();
        this.hooks.log(`${u?.name ?? ''} → ${target}：${e.ougi ? '奥義 ' : ''}${e.skill ?? '攻撃'} ${e.dmg}`);
        break;
      }
      case 'cast': {
        // 自分を中心に放つ術：名前とカットイン → エフェクト
        const u = unit(e.id);
        this.narrate(`${u?.name ?? ''}｜${e.name}`, u?.side);
        v.popupUnit(e.id, e.name, e.ougi ? 'skill-name ougi' : 'skill-name');
        if (u && (u.kind === 'hero' || u.kind === 'rival')) {
          sfx(e.ougi ? 'ougiCharge' : 'cutin');
          await this.hooks.cutIn(u.name, e.name, u.side, u.charId ?? u.fx, !!e.ougi, u.enemyId);
        }
        this.lastSkill = true;
        await v.castFx(e.jutsu, e.id, e.cells, !!e.ougi, () => sfx(e.ougi ? 'ougi' : 'skill'));
        this.hooks.log(`${u?.name ?? ''}：${e.ougi ? '奥義 ' : ''}${e.name}`);
        break;
      }
      case 'leap':
      case 'push': {
        // 縮地で跳ぶ・つむじ風で押し出される（途中のマスは塗らない）
        sfx('move', e.t === 'leap' ? 0.8 : 0.5, vary());
        await v.moveUnit(e.id, [e.from, e.to], [], null);
        const bu = this.board.units.find((x) => x.id === e.id);
        if (bu) bu.cell = e.to;
        break;
      }
      case 'zone': {
        // 術の陣が張られた・消えた：盤の表示を正本から読み直す
        this.board = this.snap();
        if (v.vm) v.vm.board.zones = this.board.zones;
        if (e.cells.length) this.narrate(`${e.kind === 'lantern' ? '灯籠陣' : '狐火の陣'}（${e.turns}手番）`, e.side);
        break;
      }
      case 'armor': {
        // 重装の鎧が剥がれた
        const u = unit(e.id);
        if (u?.etype) u.etype = { ...u.etype, armor: e.left };
        const bu = this.board.units.find((x) => x.id === e.id);
        if (bu?.etype) bu.etype = { ...bu.etype, armor: e.left };
        v.popupUnit(e.id, e.left > 0 ? '鎧が欠けた' : '鎧破り', 'info');
        sfx('armor', e.left > 0 ? 0.6 : 1);
        break;
      }
      case 'counter':
        v.popupUnit(e.id, '反撃', 'info');
        sfx('counter');
        break;
      case 'hit': {
        const u = unit(e.id);
        if (u) {
          u.hp = e.hp;
          v.flash(e.id);
          // 術の一撃は金の大きな数字と、一瞬の溜め（ヒットストップ）
          v.popupUnit(e.id, `-${e.dmg}`, this.lastSkill ? 'big crit' : e.dmg >= 40 ? 'big' : 'dmg');
          if (this.lastSkill) v.shake(8);
          else if (e.dmg >= 40) v.shake(5);
          // 大きな一撃は重い打撃音を重ねる
          if (this.lastSkill || e.dmg >= 40) sfx('hitHeavy', 0.8, vary(0.04));
          this.hooks.changed();
        }
        await v.wait(this.lastSkill ? 220 : 140);
        this.lastSkill = false;
        break;
      }
      case 'splash':
        v.splash(e.cell);
        break;
      case 'paint':
        if (v.vm) for (const c of e.cells) v.vm.owners[c] = e.side;
        break;
      case 'coreHit':
        if (v.vm) v.vm.cores[e.side] = e.hp;
        v.popupAt(this.board.cores[e.side].cell, `-${e.dmg}`, 'big');
        v.coreFx(this.board.cores[e.side].cell, e.hp <= 0);
        v.shake(e.hp <= 0 ? 14 : 7);
        sfx('coreHit');
        this.narrate(`${e.side === 'red' ? '敵本陣' : '自本陣'}に ${e.dmg} ダメージ｜残り ${Math.max(0, e.hp)}`, e.side === 'red' ? 'blue' : 'red', true);
        this.hooks.changed();
        await v.wait(200);
        break;
      case 'coreRepair':
        if (v.vm) v.vm.cores[e.side] = e.hp;
        v.popupAt(this.board.cores[e.side].cell, `+${e.amount}`, 'heal');
        await v.wait(150);
        break;
      case 'coreDown':
        sfx('coreDown');
        await v.collapseCore(e.side);
        break;
      case 'death': {
        const u = unit(e.id);
        if (u) {
          if (u.kind !== 'building') {
            const text = u.kind === 'hero' ? `${u.name}が戦闘不能` : `${u.name}を撃破`;
            this.hooks.log(text);
            this.narrate(text, u.side === 'blue' ? 'red' : 'blue', true);
            v.popupUnit(e.id, u.kind === 'hero' ? '戦闘不能' : '撃破', 'ko');
            const big = u.kind === 'rival' || u.role === 'boss' || u.kind === 'hero';
            if (u.cell != null && !v.ko(u.cell, big)) v.burst(u.cell, 'burst', '#ffd35a');
          }
          if (this.lastAttackSide === 'blue' && u.side !== 'blue' && u.cell != null) this.hooks.soul(u.cell);
          // 敵の大物＝大きな撃破音、味方が倒れた＝重い音、それ以外＝撃破（建物は軽く）
          if (u.kind === 'building') sfx('kill', 0.4, 0.8);
          else if (u.side === 'blue') sfx('down');
          else if (u.kind === 'rival' || u.role === 'boss') sfx('killBig');
          else sfx('kill', 0.8, vary());
          // 消え方：少し浮き上がりながら薄れる（魂が抜ける）
          await v.tween(u.kind === 'building' ? 240 : 320, (k) => {
            u.alpha = 1 - k;
            if (u.kind !== 'building') u.oy = -k * v.s * 0.35;
          });
          u.alive = false;
        }
        break;
      }
      case 'respawn': {
        const u = unit(e.id);
        if (u) {
          u.cell = e.cell;
          u.hp = e.hp;
          u.alive = true;
          u.alpha = 1;
          v.popupUnit(e.id, '復活', 'info');
          if (u.side === 'blue') sfx('recall', 0.7);
        }
        break;
      }
      case 'capture':
        if (v.vm) v.vm.owners[e.cell] = e.side;
        v.popupAt(e.cell, '占領', 'gold');
        v.capture(e.cell);
        this.hooks.log(e.side === 'blue' ? '龍脈を占領' : '龍脈を奪われた');
        this.narrate(e.side === 'blue' ? '龍脈を占領！ 陣地が広がった' : '龍脈を奪われた', e.side, true);
        sfx(e.side === 'blue' ? 'capture' : 'lost');
        this.hooks.changed();
        break;
      case 'build': {
        this.narrate(`${e.side === 'blue' ? '味方' : '敵'}：${BUILDING_INFO[e.type]?.name ?? '建物'}を建築`, e.side);
        v.built(e.cell);
        v.popupAt(e.cell, BUILDING_INFO[e.type]?.name ?? '建築', 'info');
        sfx('build', 0.8);
        this.board = this.snap();
        const nb = this.board.units.find((x) => x.id === e.bid);
        if (nb && v.vm) v.vm.units.set(nb.id, { ...nb, alpha: 1, flash: 1, ox: 0, oy: 0, px: null, py: null });
        if (e.side === 'blue') this.hooks.log(`${BUILDING_INFO[e.type]?.name ?? '建物'}を建てた`);
        await v.wait(220);
        break;
      }
      case 'towerShot': {
        const tower = unit(e.id);
        const target = [...(v.vm?.units.values() ?? [])].find(u => u.alive && u.cell === e.cell);
        this.narrate(`${tower?.name ?? '櫓'} → ${target?.name ?? '対象'}｜援護射撃`, tower?.side);
        v.focusAttack(e.id, e.cell);
        // 音は建物ごと：大筒はドーン、焙烙台は焙烙玉の爆発、ほかは矢
        const bt = tower?.btype;
        await v.attackFx(e.id, e.cell, false, false, () => (bt === 'ozutsu' ? sfx('cannon', 0.9, vary(0.03)) : bt === 'horoku' ? sfx('horoku', 0.8, vary()) : sfx('tower', 0.6, vary())));
        if (bt === 'ozutsu') v.shake(5);
        break;
      }
      case 'trap':
        v.popupAt(e.cell, '落とし穴！', 'big');
        sfx('trap');
        v.shake(6);
        await v.wait(240);
        break;
      case 'kitsunebi':
        v.popupUnit(e.id, '燃焼', 'info');
        sfx('burn', 0.7);
        await v.wait(120);
        break;
      case 'burn':
        v.popupUnit(e.id, '火傷', 'info');
        sfx('burn', 0.5);
        await v.wait(120);
        break;
      case 'status': {
        const u = unit(e.id);
        if (u) {
          const prev = u.status?.find((x) => x.key === e.key);
          u.status = (u.status ?? []).filter((x) => x.key !== e.key);
          if (e.turns > 0) u.status.push({ key: e.key, turns: e.turns });
          // かかった時だけ名前を出す（毎手番の減りは印の数字で分かる）
          if (e.turns > 0 && (!prev || e.turns > prev.turns)) {
            v.popupUnit(e.id, statusInfo(e.key).name, 'big');
            sfx('status', 0.8);
            if (u.side === 'blue') this.hooks.log(`${statusInfo(e.key).name}（${statusInfo(e.key).desc}）にかかった`);
            await v.wait(160);
          }
        }
        break;
      }
      case 'resist':
        v.popupUnit(e.id, `結界で${statusInfo(e.key).name}を防いだ`, 'heal');
        await v.wait(140);
        break;
      case 'cleanse': {
        const u = unit(e.id);
        if (u) u.status = [];
        v.popupUnit(e.id, `${e.keys.map((k) => statusInfo(k).name).join('・')}が治った`, 'heal');
        await v.wait(140);
        break;
      }
      case 'heal': {
        const u = unit(e.id);
        if (u && u.cell != null) {
          u.hp = Math.min(u.max, u.hp + e.amount);
          // 盤の上は❤＋量だけ。出どころ（妖力でHP+50 など）は下の案内とログに
          v.popupHeal(e.id, e.amount);
          const text = `${u.name}：${WHY[e.why] ? `${WHY[e.why]}で` : ''}HP+${e.amount}`;
          this.hooks.log(text);
          this.narrate(text, u.side);
          v.heal(u.cell);
          if (u.side === 'blue') sfx('heal', 0.7);
          await v.wait(110);
        }
        break;
      }
      case 'mat':
        if (e.side === 'blue' && this.hero) v.popupUnit(this.hero.id, `陣材+${e.amount}`, 'exp');
        break;
      case 'chest': {
        // 宝箱：中身の種類を大きく（量は結果画面で）
        const label = CHEST_LABEL[e.loot] ?? '宝';
        v.popupAt(e.cell, `宝箱：${label}`, 'big');
        v.burst(e.cell, 'burst', e.loot === 'equip' ? '#ffd35a' : e.loot === 'reidama' ? '#7fe0ff' : '#ffe9a0');
        sfx('capture', 0.8);
        this.hooks.log(`宝箱を開けた：${label}`);
        this.narrate(`宝箱を開けた：${label}（戦闘の後に受け取り）`, 'blue', true);
        await v.wait(350);
        break;
      }
      case 'territory':
        // 陣地の段階は上の陣地のバー（自陣/敵陣の%と段の目盛り）で分かるので、知らせは出さない（手動・オートとも）
        break;
      case 'potion':
        v.popupUnit(e.id, '薬', 'heal');
        sfx('potion');
        break;
      case 'item': {
        const d = itemInfo(e.item);
        v.popupUnit(e.id, d.name, 'heal');
        const u = unit(e.id);
        if (u?.cell != null) v.burst(u.cell, 'heal', '#ffd27a');
        if (u?.side === 'blue') {
          sfx('heal', 0.6);
          this.hooks.log(`${d.name}を使った`);
        }
        await v.wait(160);
        break;
      }
      case 'defend':
        v.popupUnit(e.id, '待機', 'info');
        break;
      case 'delay':
        v.popupUnit(e.id, '遅延', 'info');
        break;
      case 'intercept':
        v.popupUnit(e.id, '迎撃', 'info');
        v.flash(e.id);
        await v.wait(120);
        break;
      case 'siege':
        // 防衛戦は敵城が無いので、結界の演出・告知は出さない
        if (this.board.defense) break;
        // 自分が番人を倒した：敵の城の結界が割れる（本陣への攻撃3回まで1.5倍）。敵が倒したときは知らせだけ（自分の城に結界は無い）
        if (e.side === 'blue') {
          v.barrierBreak('red');
          sfx('telegraph');
        }
        await this.hooks.banner(e.side === 'blue' ? '敵城の結界が破れた！' : '敵が番人を倒した！', e.side === 'blue' ? '本陣への攻撃 3回まで1.5倍' : '敵の本陣への攻撃 3回まで1.5倍');
        break;
      case 'charge': {
        // 敵の武将の構え（版3）：次の手番に術が来る。このあと telegraph で射程のマスが赤く光る
        const u = unit(e.id);
        if (!e.skill) break;
        this.narrate(`${u?.name ?? '敵将'}｜${e.skill}の構え`, u?.side);
        v.popupUnit(e.id, `${e.skill}の構え`, 'skill-name');
        await v.wait(200);
        break;
      }
      case 'telegraph':
        v.popupUnit(e.id, '!!', 'big');
        sfx('telegraph');
        if (v.vm) {
          const u = unit(e.id);
          if (u) u.telegraph = e.cells;
        }
        await v.wait(300);
        break;
      case 'strike':
        for (const c of e.cells) v.burst(c, 'burst', '#ff624c');
        v.shake(9);
        sfx('bossStrike');
        await v.wait(250);
        break;
      case 'wave': {
        // 寝返った野良は敵側の色に変わる
        this.board = this.snap();
        if (v.vm) for (const id of e.defected) {
          const n = this.board.units.find((x) => x.id === id);
          const cur = v.vm.units.get(id);
          if (n && cur) v.vm.units.set(id, { ...cur, side: n.side, flash: 1 });
        }
        v.shake(6);
        sfx('wave');
        await this.hooks.banner(`第${e.n}波 襲来！`, e.defected.length ? `野良の妖 ${e.defected.length}体が寝返った` : e.n === e.total ? '頭目が攻めてくる' : '本陣を守れ');
        this.hooks.log(`第${e.n}波が来た${e.defected.length ? `（寝返り${e.defected.length}体）` : ''}`);
        break;
      }
      case 'spawn':
        this.board = this.snap();
        {
          const n = this.board.units.find((x) => x.id === e.id);
          if (n && v.vm) v.vm.units.set(n.id, { ...n, alpha: 1, flash: 1, ox: 0, oy: 0, px: null, py: null });
        }
        if (this.board.defense) {
          // 防衛戦の攻め手はまとめて出るので、1体ずつは短く
          v.popupAt(e.cell, '襲来', 'big');
          await v.wait(120);
        } else {
          v.popupAt(e.cell, e.siege ? '攻城' : '増援', 'big');
          v.shake(5);
          if (e.siege === 'blue') this.narrate(`${ENEMIES[this.board.units.find((x) => x.id === e.id)?.enemyId ?? '']?.name ?? '攻城の妖'}が我が本陣へ向かっています`, 'red', true);
          await v.wait(300);
        }
        break;
      case 'companion': {
        // 加勢・加護・スキルの能力が働いた（小さな浮き文字）
        const c = this.save.setup.loadout.companion;
        if (e.ability === 'ougi' && c && this.hooks.ougi) {
          this.hooks.log(`${shikiFormName(c.enemyId, c.star ?? 1)}を召喚`);
          sfx('ougiCharge');
          await this.hooks.ougi(c.enemyId, c.level ?? 1, v.unit(e.id));
          break;
        }
        const text = companionEventText(e.ability);
        // スキル（系統の技）から来た能力は、お供が同じ能力を持っていなければお供の名前を付けない
        const fromSkill = (this.save.setup.loadout.flags ?? []).some((f) => f.startsWith(`comp:${companionEventKey(e.ability)}=`)) && !companionHas(c?.enemyId, e.ability);
        const name = c && !fromSkill ? shikiFormName(c.enemyId, c.star ?? 1) : '';
        if (text) v.popupUnit(e.id, `${name}${name ? '・' : ''}${text}`, 'gold');
        if (e.ability === 'kasa') sfx('capture', 0.6);
        await v.wait(e.ability === 'kasa' ? 220 : 120);
        break;
      }
      case 'dash': {
        const u = unit(e.id);
        v.popupUnit(e.id, '一撃離脱', 'gold');
        if (this.hooks.dash) await this.hooks.dash(e, u);
        else await v.wait(200);
        break;
      }
      case 'reset':
        {
          const u = unit(e.id);
          if (u) {
            u.cell = e.cell;
            u.hp = e.hp;
          }
        }
        break;
      case 'skillDice': {
        const u = unit(e.id);
        this.narrate(`${u?.name ?? '妖'}：術の賽 ${e.dice.join('・')} → 威力×${e.mult.toFixed(2)}${e.zorome ? '｜ゾロ目！' : ''}`, u?.side, e.zorome);
        await this.hooks.skillDice?.(e.dice, e.mult, e.zorome, u?.side !== 'blue');
        break;
      }
      default:
        break;
    }
  }

  // ---------- 手番の操作 ----------
  private beginTurn() {
    this.board = this.snap();
    const legal = legalOf(this.api, this.b);
    const p = this.b.phase;
    const phase: Phase = p === 'rolled' ? 'rolled' : p === 'moved' ? 'moved' : 'start';
    this.ui = { phase, legal, sel: null, route: null, target: null, mode: null, build: null, forecast: null };
    if (!this.board.ougi?.ready) this.armed = false;
    this.render();
  }

  private clearSelection() {
    if (!this.ui) return;
    this.ui.sel = null;
    this.ui.route = null;
    this.ui.target = null;
    this.ui.mode = null;
    this.ui.build = null;
    this.ui.forecast = null;
    this.ui.castCell = null;
  }

  targets(mode: 'attack' | 'skill' | 'ougi') {
    return this.ui ? (mode === 'attack' ? this.ui.legal.attacks : mode === 'ougi' ? this.ui.legal.ougis : this.ui.legal.skills) : [];
  }
  /** 術の枠の中身：構えていれば奥義 */
  private get skillMode(): 'skill' | 'ougi' {
    return this.armed ? 'ougi' : 'skill';
  }

  /** メダリオンの長押し：奥義を構える／解く。構えられたら true */
  arm(on = !this.armed): boolean {
    if (!this.ui || this.busy || this.auto || this.paused) return false;
    if (on && !this.board.ougi?.ready) return false;
    this.armed = on;
    if (this.ui.mode === 'skill' || this.ui.mode === 'ougi') {
      this.ui.target = null;
      this.ui.mode = null;
      this.hooks.info(null);
    }
    this.render();
    return true;
  }

  /** 盤面の強調表示を UI 状態に合わせる */
  render() {
    const v = this.view;
    const ui = this.ui;
    const hl = emptyHighlights();
    if (ui && !this.auto) {
      const hero = this.hero;
      if (ui.build) {
        const all = ui.legal.builds.get(ui.build) ?? new Set<number>();
        const lk = this.lock?.cells;
        hl.build = lk ? new Set(lk.filter((c) => all.has(c))) : all;
      } else if (ui.mode === 'cast') {
        // 術：選べるマス（縮地・送り火）と、効く範囲の下見
        const info = this.board.cast;
        const ougi = this.armed;
        if (info?.aim) hl.castCells = new Set(this.api.castCells(this.b, ougi));
        hl.castArea = new Set(this.api.castArea(this.b, ougi, ui.castCell ?? undefined));
        if (ui.castCell != null) hl.sel = ui.castCell;
      } else if (ui.target) {
        const mode = ui.mode ?? 'attack';
        const skill = mode !== 'attack';
        const ougi = mode === 'ougi';
        const list = this.targets(mode);
        hl.targets = list.map((t) => ({ cell: refCell(this.board, t), core: t.kind === 'core', selected: sameRef(t, ui.target) }));
        for (const t of list) {
          const f = this.api.forecast(this.b, t, skill, undefined, ougi);
          const max = t.kind === 'core' ? this.board.cores[t.side].max : (this.board.units.find((u) => u.id === t.id)?.max ?? 1);
          if (f) hl.previews.push({ cell: refCell(this.board, t), hp: f.hpBefore, after: f.hpAfter, max, selected: sameRef(t, ui.target) });
        }
        ui.forecast = this.api.forecast(this.b, ui.target, skill, undefined, ougi);
        for (const s of ui.forecast?.splash ?? []) {
          const su = this.board.units.find((x) => x.id === s.id);
          if (su?.cell != null) hl.previews.push({ cell: su.cell, hp: su.hp, after: Math.max(0, su.hp - s.dmg), max: su.max, selected: false, splash: true });
        }
      } else {
        const seen = new Set<string>();
        for (const t of [...ui.legal.attacks, ...(this.armed ? ui.legal.ougis : ui.legal.skills)]) {
          const k = t.kind === 'core' ? `c${t.side}` : `a${t.id}`;
          if (seen.has(k)) continue;
          seen.add(k);
          hl.targets.push({ cell: refCell(this.board, t), core: t.kind === 'core', selected: false });
        }
        if (ui.phase === 'rolled') {
          // 案内で行き先を1マスに決めているときは、そのマスだけ光らせる（ほかは押しても動かない）
          const lk = this.lock?.cells;
          hl.reach = lk ? new Set(lk.filter((c) => ui.legal.moves.has(c))) : ui.legal.moves;
          hl.sel = ui.sel;
          hl.path = ui.route;
          if (ui.sel != null && hero && ui.sel !== hero.cell) hl.ghost = { cell: ui.sel, unit: hero };
          if (ui.sel != null) hl.targets = [];
        }
      }
    }
    hl.info = v.hl.info;
    hl.dash = !!ui?.legal.dashing;
    v.hl = hl;
    this.hooks.changed();
  }

  private async exec(cmd: Command) {
    if (!this.ui || this.busy) return false;
    this.busy = true;
    try {
      const r = this.api.apply(this.b, cmd);
      if (!r.ok) {
        sfx('error');
        this.hooks.error(errorLabel(r.error));
        return false;
      }
      this.commands.push(cmd);
      this.clearSelection();
      this.view.hl = emptyHighlights();
      this.hooks.info(null);
      this.ui = null;
      this.hooks.changed();
      await this.play(r.events, () => true);
      this.persist();
      const done = this.resolveInput;
      this.resolveInput = null;
      done?.();
      return true;
    } finally {
      this.busy = false;
    }
  }

  /** 防衛：攻め手を倒しきって次の波を待つ間（次の波まで手番終了を続けられる） */
  private skipWave = false;
  private skipHp = 0;
  canSkipWave() {
    const d = this.board.defense;
    const hero = this.hero;
    if (!d || this.auto || this.over || !hero || d.wave < 1 || d.wave >= d.total || d.attackers > 0 || !d.nextIn) return false;
    return !this.skipWave || hero.hp >= this.skipHp;
  }
  get skippingWave() {
    return this.skipWave;
  }
  /** 「次の波へ」：波が来る・自分が攻撃されるまで手番終了を続ける。もう一度押すと止める */
  skipToWave() {
    if (this.skipWave) {
      this.skipWave = false;
      return this.hooks.changed();
    }
    if (!this.canSkipWave() || !this.ui || this.busy) return;
    this.skipWave = true;
    this.skipHp = this.hero?.hp ?? 0;
    this.hooks.changed();
    void this.exec({ t: 'wait' });
  }

  /** HUD のボタンから */
  async command(name: string, arg?: string) {
    const ui = this.ui;
    if (!ui || this.busy || this.paused || this.auto) return;
    const hero = this.hero;
    switch (name) {
      case 'roll':
        return void this.exec({ t: 'roll' });
      case 'demolish':
        if (arg) {
          this.hooks.info(null);
          return void this.exec({ t: 'demolish', cell: Number(arg) });
        }
        return;
      case 'wait':
        return void this.exec({ t: 'wait' });
      case 'potion':
        return void this.exec({ t: 'potion' });
      case 'item':
        return void this.exec({ t: 'item', id: arg as ItemId });
      case 'stay':
        if (hero?.cell != null) return void this.exec({ t: 'move', to: hero.cell });
        return;
      case 'move':
        if (ui.sel != null) return void this.exec({ t: 'move', to: ui.sel, ...(ui.route && ui.route[ui.route.length - 1] === ui.sel ? { route: ui.route } : {}) });
        return;
      case 'attack':
      case 'skill':
      case 'ougi': {
        if (name === 'ougi' && !this.armed) return;
        // チュートリアルで手段を決めているとき（攻撃で倒す・術で倒すなど）は、ほかの手段を使わせない
        if (this.lock?.mode && this.lock.mode !== name) return;
        const all = this.targets(name);
        const lt = this.lock?.target;
        const list = lt ? all.filter((t) => sameRef(t, lt)) : all;
        const chosen = list.find((t) => sameRef(t, ui.target));
        if (ui.mode === name && chosen) {
          if (name === 'ougi') {
            const done = await this.exec({ t: 'attack', target: chosen, skill: true, ougi: true });
            if (done) this.armed = false;
            return;
          }
          return void this.exec({ t: 'attack', target: chosen, ...(name === 'skill' ? { skill: true } : {}) });
        }
        if (list.length) {
          // 選んでいた相手がこの手段でも届くならそのまま。初めて選ぶときは、攻撃と術のどちらでも届く相手を先に（切り替えて見込みを比べられる）
          const other = name === 'attack' ? this.targets(this.armed ? 'ougi' : 'skill') : this.targets('attack');
          ui.mode = name;
          ui.target = chosen ?? list.find((t) => other.some((o) => sameRef(o, t))) ?? list[0];
          ui.sel = null;
          ui.route = null;
          ui.build = null;
          this.hooks.info(refCell(this.board, ui.target));
          this.render();
        }
        return;
      }
      case 'build':
        if (arg && ui.legal.builds.has(arg as BuildingType)) {
          this.clearSelection();
          ui.build = arg as BuildingType;
          this.render();
        }
        return;
      case 'cast': {
        // 自分を中心に放つ術：1回目で範囲の下見（マスを選ぶ術は選ぶ）、もう一度押すと放つ
        const info = this.board.cast;
        const ougi = this.armed;
        if (!info || !(ougi ? ui.legal.castOugi : ui.legal.cast)) return;
        if (ui.mode === 'cast' && (!info.aim || ui.castCell != null)) return void this.castNow(ui.castCell ?? undefined, ougi);
        this.clearSelection();
        ui.mode = 'cast';
        this.hooks.info(null);
        this.render();
        return;
      }
      case 'cancel':
        this.clearSelection();
        this.hooks.info(null);
        this.render();
        return;
    }
  }

  // ---------- 盤面の入力 ----------
  readonly input: InputHandlers = {
    dragStart: (cell) => {
      const ui = this.ui;
      const hero = this.hero;
      if (!ui || this.busy || this.paused || this.auto || ui.phase !== 'rolled' || ui.build || ui.target || !hero || this.lock) return null;
      if (ui.route && ui.route[ui.route.length - 1] === cell) return ui.route.slice();
      if (cell === hero.cell) return [cell];
      return null;
    },
    dragStep: (path, cell) => {
      const next = this.stepRoute(path, cell);
      if (!next) return null;
      this.setRoute(next);
      return next;
    },
    dragEnd: () => this.render(),
    tap: (cell) => this.tap(cell),
    key: (k) => {
      if (!this.ui || this.auto) return false;
      const ui = this.ui;
      if (k === ' ' || k === 'r') {
        if (ui.legal.roll) void this.command('roll');
        return true;
      }
      if (k === 'Enter') {
        if (ui.target && ui.mode) void this.command(ui.mode);
        else if (ui.sel != null) void this.command('move');
        return true;
      }
      if (k === 'Escape') return void this.command('cancel'), true;
      if (k === 'a') return void this.command('attack'), true;
      if (k === 's') return void this.command(this.skillMode), true;
      if (k === 'w') return void this.command('wait'), true;
      return false;
    },
  };

  /** 経路に1マス足す／1マス戻す（なぞり・1マスずつのタップ共通）。できなければ null */
  private stepRoute(path: number[], cell: number): number[] | null {
    const ui = this.ui;
    if (!ui) return null;
    const next = path.slice();
    if (next.length > 1 && cell === next[next.length - 2]) next.pop();
    else if (!next.includes(cell) && adjacent(next[next.length - 1], cell) && ui.legal.pass.has(cell) && this.api.validRoute(this.b, [...next, cell], true)) next.push(cell);
    else return null;
    return next;
  }

  private async castNow(cell: number | undefined, ougi: boolean) {
    const done = await this.exec({ t: 'cast', ...(cell != null ? { cell } : {}), ...(ougi ? { ougi: true } : {}) });
    if (done && ougi) this.armed = false;
  }

  private setRoute(path: number[]) {
    const ui = this.ui!;
    ui.route = path.length > 1 ? path : null;
    ui.sel = path.length > 1 && ui.legal.moves.has(path[path.length - 1]) ? path[path.length - 1] : null;
    ui.target = null;
    ui.mode = null;
    this.hooks.info(null);
    this.render();
  }

  private tap(cell: number) {
    const ui = this.ui;
    sfx('select', 0.45, vary(0.05));
    if (!ui || this.busy || this.auto) {
      this.hooks.info(cell);
      return;
    }
    if (ui.mode === 'cast' && this.board.cast?.aim) {
      // 術のマスを選ぶ：選んだマスをもう一度押すと放つ
      if (!this.api.castCells(this.b, this.armed).includes(cell)) return void this.hooks.info(cell);
      if (ui.castCell === cell) return void this.castNow(cell, this.armed);
      ui.castCell = cell;
      this.render();
      return;
    }
    if (ui.build) {
      const cells = ui.legal.builds.get(ui.build);
      if (this.lock?.cells && !this.lock.cells.includes(cell)) return;
      if (cells?.has(cell)) void this.exec({ t: 'build', type: ui.build, cell });
      else this.hooks.info(cell);
      return;
    }
    // チュートリアルの一本道：決めたマス・相手のほかは押しても何もしない
    if (this.lock) {
      const lk = this.lock;
      if (ui.phase === 'rolled' && lk.cells) {
        if (!lk.cells.includes(cell) || !ui.legal.moves.has(cell)) return;
        if (ui.sel === cell) return void this.command('move');
        ui.sel = cell;
        ui.target = null;
        ui.mode = null;
        ui.route = this.api.routeTo(this.b, cell);
        if (ui.route.length < 2) ui.route = null;
        this.render();
        return;
      }
      if (lk.target) {
        const at = refCell(this.board, lk.target);
        if (cell !== at) return;
        const mode = lk.mode ?? (this.armed ? 'ougi' : ui.legal.skills.some((t) => sameRef(t, lk.target)) ? 'skill' : 'attack');
        if (sameRef(ui.target, lk.target) && ui.mode === mode) return void this.command(mode);
        ui.target = lk.target;
        ui.mode = mode;
        ui.sel = null;
        ui.route = null;
        this.hooks.info(cell);
        this.render();
        return;
      }
      return;
    }
    const all: { t: TargetRef; mode: 'attack' | 'skill' | 'ougi' }[] = [
      ...ui.legal.attacks.map((t) => ({ t, mode: 'attack' as const })),
      ...(this.armed ? ui.legal.ougis.map((t) => ({ t, mode: 'ougi' as const })) : ui.legal.skills.map((t) => ({ t, mode: 'skill' as const }))),
    ];
    const hit = all.filter((x) => refCell(this.board, x.t) === cell);
    if (hit.length && !(ui.phase === 'rolled' && ui.sel != null)) {
      const keep = hit.find((x) => x.mode === ui.mode) ?? hit[0];
      if (sameRef(ui.target, keep.t) && ui.mode === keep.mode) {
        void this.command(keep.mode); // 同じ対象をもう一度押すと実行
        return;
      }
      ui.target = keep.t;
      ui.mode = keep.mode;
      ui.sel = null;
      ui.route = null;
      this.hooks.info(cell);
      this.render();
      return;
    }
    if (ui.phase === 'rolled') {
      const hero = this.hero;
      if (ui.sel === cell && ui.legal.moves.has(cell)) return void this.command('move');
      // 1マスずつのタップで経路を選ぶ：経路の終点（無ければ自分）の隣を押すと伸び、1つ前を押すと戻る。経路の途中を押すとそこまで縮む
      if (hero?.cell != null) {
        const path = ui.route ?? [hero.cell];
        const at = path.indexOf(cell);
        if (at > 0 && at < path.length - 2) return void this.setRoute(path.slice(0, at + 1));
        if (cell === hero.cell && ui.route) return void this.setRoute([hero.cell]);
        const next = this.stepRoute(path, cell);
        if (next) return void this.setRoute(next);
      }
      if (ui.legal.moves.has(cell)) {
        ui.sel = cell;
        ui.target = null;
        ui.mode = null;
        ui.route = this.api.routeTo(this.b, cell);
        if (ui.route.length < 2) ui.route = null;
        this.hooks.info(null);
        this.render();
        return;
      }
      // 届かない敵を押したら、隣の一番近い移動先を選ぶ
      const enemy = this.board.units.find((u) => u.alive && u.cell === cell && u.side !== 'blue' && !u.hidden);
      if ((enemy || (cell === this.board.cores.red.cell && !this.board.defense)) && hero?.cell != null) {
        const near = [...ui.legal.moves].filter((c) => dist(c, cell) <= 1).sort((a, b) => dist(a, hero.cell!) - dist(b, hero.cell!) || a - b);
        if (near.length) {
          ui.sel = near[0];
          ui.route = this.api.routeTo(this.b, near[0]);
          this.render();
          this.hooks.info(cell);
          return;
        }
      }
    }
    if (ui.target || ui.sel != null) {
      this.clearSelection();
      this.render();
    }
    this.hooks.info(cell);
  }
}

function errorLabel(code: string) {
  const map: Record<string, string> = {
    phase: '今はその操作はできません',
    turn: '自分の手番ではありません',
    range: '届きません',
    target: 'その相手は狙えません',
    path: 'そこへは動けません',
    route: 'その経路は通れません',
    reach: 'そこへは動けません',
    cooldown: '術はまだ使えません',
    surge: '奥義は妖力が参のときに放てます',
    seal: '封印中は術・奥義を使えません',
    skill: '術はまだ使えません',
    potions: '薬がありません',
    used: 'この手番はもう道具を使いました',
    empty: 'もうありません',
    moved: '動いた後は使えません',
    nothing: '治す状態異常がありません',
    full: 'HPが満タンです',
    locked: '未解放の建物です',
    done: 'この手番はもう建てました',
    limit: '建てられる数の上限です',
    mat: '陣材が足りません',
    cell: 'そこには建てられません',
    cells: '建てられるマスがありません',
  };
  return map[code] ?? `その操作はできません（${code}）`;
}
